import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X } from 'lucide-react';
import { helpers } from '../../../utils/helpers';
import './DriverMapView.css';
import { getMapboxToken } from '../../../utils/utils';

mapboxgl.accessToken = getMapboxToken();

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const DriverMapView = ({ drivers = [], onClose }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);

  const driversWithLocation = drivers.filter(d => {
    const coords = d.location?.coordinates;
    return coords && Array.isArray(coords) && coords.length === 2 &&
      !(coords[0] === 0 && coords[1] === 0);
  });

  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [51.427533, 25.200859], // Default Qatar center
      zoom: 10
    });

    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update markers when drivers change
  useEffect(() => {
    if (!map.current) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    if (driversWithLocation.length === 0) return;

    const bounds = new mapboxgl.LngLatBounds();

    driversWithLocation.forEach(driver => {
      const [lng, lat] = driver.location.coordinates;
      const updatedAt = driver.location?.updatedAt;
      const isStale = !updatedAt || (Date.now() - new Date(updatedAt).getTime() > STALE_THRESHOLD_MS);
      const relTime = helpers.getRelativeTime(updatedAt);
      const driverColor = driver.color || '#FE8403';

      const el = document.createElement('div');
      el.className = 'driver-map-marker';

      const dot = document.createElement('div');
      dot.className = `driver-map-marker-dot ${isStale ? 'stale' : ''}`;
      dot.style.backgroundColor = isStale ? '#9ca3af' : driverColor;
      dot.textContent = driver.name || '';

      const pin = document.createElement('div');
      pin.className = 'driver-map-marker-pin';

      const label = document.createElement('div');
      label.className = `driver-map-marker-label ${isStale ? 'stale' : ''}`;
      label.textContent = relTime === 'N/A' ? 'No data' : `${relTime}`;

      el.appendChild(dot);
      el.appendChild(pin);
      el.appendChild(label);

      const popupHTML = `
        <div class="driver-map-popup-content">
          <div class="driver-map-popup-name">
            <span class="driver-map-popup-color-dot" style="background:${driverColor}"></span>
            ${driver.name || 'Unknown'}
          </div>
          <div class="driver-map-popup-row"><strong>Phone:</strong> ${driver.phoneNumber || 'N/A'}</div>
          <div class="driver-map-popup-row"><strong>Last Update:</strong> ${helpers.formatQatarTime(updatedAt)}</div>
          <div class="driver-map-popup-row"><strong>Relative:</strong> ${relTime === 'N/A' ? 'No data' : relTime + ' ago'}</div>
        </div>
      `;

      const popup = new mapboxgl.Popup({
        offset: 25,
        className: 'driver-map-popup',
        closeButton: true,
        maxWidth: '240px'
      }).setHTML(popupHTML);

      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map.current);

      markersRef.current.push(marker);
      bounds.extend([lng, lat]);
    });

    if (driversWithLocation.length === 1) {
      const [lng, lat] = driversWithLocation[0].location.coordinates;
      map.current.flyTo({ center: [lng, lat], zoom: 14 });
    } else {
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 14 });
    }
  }, [drivers]);

  return (
    <div className="driver-dialog-overlay">
      <div className="driver-map-modal">
        <div className="driver-dialog-header">
          <h2>Delivery Staff Locations</h2>
          <button className="driver-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="driver-map-body">
          <div className="driver-map-container">
            <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
            {driversWithLocation.length === 0 && (
              <div className="driver-map-no-location">
                No drivers with location data available
              </div>
            )}
          </div>
        </div>
        <div className="driver-form-actions">
          <button
            type="button"
            className="driver-cancel-btn"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverMapView;
